import * as $ from 'jquery';

import { IEventAggregator } from 'common/barrels/core';
import { IResourceProvider } from 'common/resourceprovider';
import { ControlArgs } from 'runtime/controls/controlargs';
import { Control } from 'runtime/controls/control';
import { ActivityButton } from 'runtime/controls/activitybutton';
import { TextBox } from 'runtime/controls/textbox';
import { NumericBox } from 'runtime/controls/numericbox';
import { DropDownList } from 'runtime/controls/dropdownlist';
import { AutoCompleteList } from 'runtime/controls/autocompletelist';
import { DateTimePicker } from 'runtime/controls/datetimepicker';
import { CheckBox } from 'runtime/controls/checkbox';

import { addImgAreaSelectToJQuery } from 'runtime/libs/imgareaselect/jquery.imgareaselect-0.9.10';
import { addTimePickerToJQuery } from 'common/libs/timepicker/jquery.timepicker';

declare global {
    export interface JQuery {
        activityButton(eventAggregator: IEventAggregator, resources: IResourceProvider, options: IActivityButtonOptions): JQuery;
        autoCompleteList(eventAggregator: IEventAggregator, resources: IResourceProvider, context: string, options?: IAutoCompleteListOptions): JQuery;
        textBox(eventAggregator: IEventAggregator, resources: IResourceProvider, options?: ITextBoxOptions): JQuery;
        numericBox(eventAggregator: IEventAggregator, resources: IResourceProvider, options?: INumericBoxOptions): JQuery;
        dropDownList(eventAggregator: IEventAggregator, resources: IResourceProvider, context: string, options?: IDropDownListOptions): JQuery;
        dateTimePicker(eventAggregator: IEventAggregator, resources: IResourceProvider, options?: IDateTimePickerOptions): JQuery;
        checkBox(eventAggregator: IEventAggregator, resources: IResourceProvider, options?: ICheckBoxOptions): JQuery;

        controlInstance(): Control;
    }
}

let augmented = false;

export function augmentJQuery() {
    if (augmented)
        return;

    augmented = true;

    const root = (<any>$).prototype;

    addImgAreaSelectToJQuery($);
    addTimePickerToJQuery($);

    const defaultControlOptions = {
        isElementVisible: true,
        isElementEnabled: true,
        margin: {
            top: '0',
            right: '0',
            bottom: '0',
            left: '0'
        },
        elementAlignment: {
            horizontal: 'stretch',
            vertical: 'stretch'
        },
        elementWidth: '100%',
        elementHeight: '100%',
        tabOrder: -1,
        'content.TextAlignment': 'left',
        'content.FontSize': 'inherit',
        'content.FontWeight': 'inherit'
    };

    const processProperties = (options: any): any => {
        var opts = {};
        if (options) {
            for (var prop in options) {
                if (options.hasOwnProperty(prop)) {
                    var p = (prop.indexOf('content') === 0) ? 'content.' + prop.substr(7) : prop;
                    opts[p] = options[prop];
                }
            }
        }

        return opts;
    };

    // controls.ActivityButton
    root.activityButton = function (eventAggregator: IEventAggregator, resources: IResourceProvider, options: IActivityButtonOptions): JQuery {
        var defaultActivityButtonOptions = {
            'content.Text': 'Button'
        };

        var activityButtonOptions = $.extend({}, defaultControlOptions, defaultActivityButtonOptions, processProperties(options));

        var args = ControlArgs.createMock(this, activityButtonOptions, eventAggregator, null, resources);

        return this.each(() => {
            // ReSharper disable once UnusedLocals
            var ab = new ActivityButton(args);
            ab.isParentEnabled(true);
        });
    };

    // controls.AutoCompleteList
    root.autoCompleteList = function (eventAggregator: IEventAggregator, resources: IResourceProvider, context: string, options?: IAutoCompleteListOptions): JQuery {
        var defaultAutoCompleteListOptions = {
            'content.MinimumPrefixLength': 3
        };

        var autoCompleteListOptions = $.extend({}, defaultControlOptions, defaultAutoCompleteListOptions, processProperties(options));

        return this.each(() => {
            var args = ControlArgs.createMock(this, autoCompleteListOptions, eventAggregator, context, resources);

            // ReSharper disable once UnusedLocals
            var acl = new AutoCompleteList(args);
            acl.isParentEnabled(true);
        });
    };

    root.textBox = function (eventAggregator: IEventAggregator, resources: IResourceProvider, options?: ITextBoxOptions): JQuery {
        var defaultTextBoxOptions = {
            //'content.AcceptsReturn': false,
            //'content.WrapText': false,
            //'content.Text': null,
            //'content.MaxLength': undefined,
            'content.UpdateBindingOnTextChanged': true
        };

        var textBoxOptions = $.extend({}, defaultControlOptions, defaultTextBoxOptions, processProperties(options));

        var args = ControlArgs.createMock(this, textBoxOptions, eventAggregator, null, resources);

        return this.each(() => {
            // ReSharper disable once UnusedLocals
            var tb = new TextBox(args);
            tb.isParentEnabled(true);
        });
    };

    // controls.NumericBox
    root.numericBox = function (eventAggregator: IEventAggregator, resources: IResourceProvider, options?: INumericBoxOptions): JQuery {
        var defaultNumericBoxOptions = {
            'content.UpdateOnEnter': true,
            'content.TextAlignment': 'right'
        };

        var numericBoxOptions = $.extend({}, defaultControlOptions, defaultNumericBoxOptions, processProperties(options));

        var args = ControlArgs.createMock(this, numericBoxOptions, eventAggregator, null, resources);

        return this.each(() => {
            // ReSharper disable once UnusedLocals
            var nb = new NumericBox(args);
            nb.isParentEnabled(true);
        });
    };

    // controls.DropDownList
    root.dropDownList = function (eventAggregator: IEventAggregator, resources: IResourceProvider, context: string, options?: IDropDownListOptions): JQuery {
        var defaultDropDownListOptions = {
            'content.IsRequiredToAlwaysLoadItemsOnAccess': false
        };

        var dropDownListOptions = $.extend({}, defaultControlOptions, defaultDropDownListOptions, processProperties(options));

        return this.each(() => {
            var args = ControlArgs.createMock(this, dropDownListOptions, eventAggregator, context, resources);

            // ReSharper disable once UnusedLocals
            var acl = new DropDownList(args);
            acl.isParentEnabled(true);
        });
    };

    // controls.DateTimePicker
    root.dateTimePicker = function (eventAggregator: IEventAggregator, resources: IResourceProvider, options?: IDateTimePickerOptions): JQuery {
        var defaultDateTimePickerOptions = {
            'content.HasDate': true,
            'content.HasTime': false
        };

        var dateTimePickerOptions = $.extend({}, defaultControlOptions, defaultDateTimePickerOptions, processProperties(options));

        var args = ControlArgs.createMock(this, dateTimePickerOptions, eventAggregator, null, resources);

        return this.each(() => {
            // ReSharper disable once UnusedLocals
            var dtp = new DateTimePicker(args);
            dtp.isParentEnabled(true);
        });
    };

    // controls.CheckBox
    root.checkBox = function (eventAggregator: IEventAggregator, resources: IResourceProvider, options?: ICheckBoxOptions): JQuery {
        var defaultCheckBoxOptions = {
            'content.IsChecked': false
        };

        var checkBoxOptions = $.extend({}, defaultControlOptions, defaultCheckBoxOptions, processProperties(options));

        var args = ControlArgs.createMock(this, checkBoxOptions, eventAggregator, null, resources);

        return this.each(() => {
            // ReSharper disable once UnusedLocals
            var cb = new CheckBox(args);
            cb.isParentEnabled(true);
        });
    };

    root.controlInstance = function (): Control {
        return Control.instance(this[0]);
    };
}

export interface IControlOptions {
    isElementEnabled?: boolean;
    isElementVisible?: boolean;
}

export interface IControlStyleOptions {
    contentTextAlignment?: string;          // = 'left'
    contentFontFamily?: string;
    contentFontSize?: string;               // = 'inherit'
    contentFontStyle?: string;
    contentFontWeight?: string;             // = 'inherit'
    contentForeground?: string;
    contentBackground?: string;
}

export interface IActivityButtonOptions extends IControlOptions, IControlStyleOptions {
    contentText: string;
    contentLanguageKey?: string;
}

export interface IAutoCompleteListOptions extends IControlOptions, IControlStyleOptions {
    contentMinimumPrefixLength?: number;
    contentMinimumPopulateDelay?: number;
}

export interface ITextBoxOptions extends IControlOptions, IControlStyleOptions {
    contentAcceptsReturn?: boolean;
    contentWrapText?: boolean;
    contentText?: string;
    contentMaxLength?: number;
    contentUpdateBindingOnTextChanged?: boolean;
}

export interface INumericBoxOptions extends IControlOptions, IControlStyleOptions {
    contentDecimalPlaces?: number;
    contentValue?: number;
}

export interface IDropDownListOptions extends IControlOptions, IControlStyleOptions {
    contentIsRequiredToAlwaysLoadItemsOnAccess?: boolean;
}

export interface ICheckBoxOptions extends IControlOptions {
    contentIsChecked: boolean;
}

export interface IDateTimePickerOptions extends IControlOptions, IControlStyleOptions {
    contentHasDate: boolean;                // = true
    contentHasTime: boolean;                // = false
}